import PropTypes from "prop-types";
import React, { useCallback, useEffect, useMemo, useRef, useState } from "react";
import { Container, Row, Col, Dropdown, DropdownToggle, DropdownMenu, DropdownItem } from "reactstrap";
import DataTable from "../../../components/RestaurantAdminComponents/DataTable";
import StatCard from "../../../components/RestaurantAdminComponents/StatCard";
import { PercentIcon, SpendIcon } from "../../../components/Common/SettlementIcons";
import peopleOrangeIcon from "../../../assets/images/restaurant-portal/people-orange.svg";
import locationOrangeIcon from "../../../assets/images/sidebar/location-orange.svg";

// API
import { get } from "../../../api/axios";
import endpoints from "../../../api/endpoints";
import { extractErrorMessage } from "../../../utils/errorHandler";

const PAGE_SIZE = 10;
const emptyValue = "—";

const PERFORMANCE_PERIODS = {
  all_time: { label: "All Time", period: "all_time" },
  weekly: { label: "Weekly", period: "weekly", groupBy: "day" },
  monthly: { label: "Monthly", period: "monthly", groupBy: "week" },
  yearly: { label: "Yearly", period: "yearly", groupBy: "month" },
};

const SUMMARY_PERIOD_LABELS = {
  all_time: "All Time",
  weekly: "This Week",
  monthly: "This Month",
  yearly: "This Year",
  custom: "Custom Range",
};

const getPeriodLabel = (period) => {
  const labels = {
    weekly: "This Week",
    monthly: "This Month",
    yearly: "This Year",
    all_time: "All Time",
    custom: "Custom Range",
  };
  return labels[period] || "This Week";
};

const currencyFormatter = new Intl.NumberFormat("en-US", {
  style: "currency",
  currency: "USD",
});

const formatMoney = (value) => {
  if (value && typeof value === "object") {
    if (value.formatted) return value.formatted;
    if (value.amount !== undefined && value.amount !== null) return formatMoney(value.amount);
    if (value.value !== undefined && value.value !== null) return formatMoney(value.value);
  }

  if (value === undefined || value === null || value === "") return currencyFormatter.format(0);

  const numeric = Number(value);
  return currencyFormatter.format(Number.isFinite(numeric) ? numeric : 0);
};

const formatNumber = (value) => {
  const numeric = Number(value ?? 0);
  return Number.isFinite(numeric) ? numeric.toLocaleString("en-US") : "0";
};

const getPercentageNumber = (value) => {
  if (value && typeof value === "object") {
    return getPercentageNumber(value.percentage ?? value.value ?? value.amount);
  }

  if (typeof value === "string") {
    const numeric = Number(value.replace("%", "").trim());
    return Number.isFinite(numeric) ? numeric : 0;
  }

  const numeric = Number(value ?? 0);
  return Number.isFinite(numeric) ? numeric : 0;
};

const formatPercentage = (value) => `${getPercentageNumber(value).toFixed(1).replace(/\.0$/, "")}%`;

const extractPayload = (response) => response?.data?.data || response?.data || {};

const formatPeriodDate = (value, includeYear = false) => {
  if (!value) return "";
  const rawValue = value.toString();
  const normalizedValue = rawValue.includes("T")
    ? rawValue
    : rawValue.includes(" ")
      ? rawValue.replace(" ", "T")
      : `${rawValue}T00:00:00`;
  const date = new Date(normalizedValue);
  if (Number.isNaN(date.getTime())) return value;
  return date.toLocaleDateString("en-US", {
    month: "short",
    day: "numeric",
    ...(includeYear ? { year: "numeric" } : {}),
  });
};

const formatLastVisitDate = (value) => {
  if (!value || value === emptyValue) return emptyValue;
  const formattedDate = formatPeriodDate(value, true);
  if (formattedDate !== value) return formattedDate;
  return value.toString().split(/[T\s]/)[0] || emptyValue;
};

/* ============ inline icons (self-contained, no new asset files needed) ============ */

const IconCalendar = () => (<svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"><rect x="3" y="5" width="18" height="16" rx="2" /><path d="M3 10h18M8 3v4M16 3v4" /></svg>);
const IconChevronDown = () => (<svg width="13" height="13" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"><path d="m6 9 6 6 6-6" /></svg>);

const getOverviewQuery = (periodKey, customDateFrom, customDateTo, pagination = {}) => {
  const config = periodKey === "custom"
    ? { period: "custom" }
    : PERFORMANCE_PERIODS[periodKey] || PERFORMANCE_PERIODS.all_time;
  const params = new URLSearchParams();
  params.append("period", config.period);
  if (config.groupBy) {
    params.append("group_by", config.groupBy);
  }
  if (config.period === "custom" && customDateFrom && customDateTo) {
    params.append("date_from", customDateFrom);
    params.append("date_to", customDateTo);
  }

  Object.entries(pagination).forEach(([key, value]) => {
    if (value !== undefined && value !== null && value !== "") {
      params.append(key, value);
    }
  });

  return params.toString();
};

const extractRows = (section) => {
  if (Array.isArray(section)) return section;
  if (Array.isArray(section?.data)) return section.data;
  if (Array.isArray(section?.rows)) return section.rows;
  if (Array.isArray(section?.items)) return section.items;
  return [];
};

const getSummaryValue = (summaryCards, keys) => {
  for (const key of keys) {
    if (Array.isArray(summaryCards)) {
      const card = summaryCards.find((item) => item?.key === key);
      if (card) return card;
      continue;
    }

    const value = summaryCards?.[key];
    if (value !== undefined && value !== null && value !== "") return value;
  }
  return 0;
};

const getSummaryCard = (summaryCards, key) => (
  Array.isArray(summaryCards)
    ? summaryCards.find((item) => item?.key === key) || {}
    : summaryCards?.[key] || {}
);

const getFirstValue = (item, keys, fallback = emptyValue) => {
  for (const key of keys) {
    const value = item?.[key];
    if (value !== undefined && value !== null && value !== "") return value;
  }
  return fallback;
};

const getPagination = (section, fallbackRows = []) => {
  const pagination = section?.pagination || {};
  const currentPage = Number(pagination.current_page ?? 1);
  const perPage = Number(pagination.per_page ?? PAGE_SIZE);
  const total = Number(pagination.total ?? fallbackRows.length);
  const lastPage = Number(pagination.last_page ?? 1);

  return {
    current_page: Number.isFinite(currentPage) ? currentPage : 1,
    per_page: Number.isFinite(perPage) ? perPage : PAGE_SIZE,
    total: Number.isFinite(total) ? total : fallbackRows.length,
    last_page: Number.isFinite(lastPage) ? lastPage : 1,
    has_more: Boolean(pagination.has_more),
  };
};

const appendUniqueRows = (currentRows, incomingRows, getKey) => {
  const toKey = (row) => {
    const value = getKey(row);
    return value === undefined || value === null || value === "" ? "" : String(value);
  };
  const seen = new Set(currentRows.map(toKey).filter(Boolean));
  const nextRows = [...currentRows];

  incomingRows.forEach((row) => {
    const key = toKey(row);
    if (key && !seen.has(key)) {
      seen.add(key);
      nextRows.push(row);
    }
  });

  return nextRows;
};

const getMemberRowKey = (row) => row.user_id ?? row.member_id ?? row.id ?? row.member_email;
const getRestaurantRowKey = (row) => (
  `${row.user_id ?? row.member_id ?? row.member_email ?? row.id ?? "member"}-${row.restaurant_name ?? row.merchant_name ?? row.restaurant ?? "restaurant"}`
);

const LeakageProgress = ({ value }) => {
  const numeric = Math.max(0, Math.min(100, getPercentageNumber(value)));

  return (
    <span className="network-leakage-progress">
      <span className="network-leakage-progress__bar" aria-hidden="true">
        <span className="network-leakage-progress__fill" style={{ width: `${numeric}%` }} />
      </span>
      <span className="network-leakage-progress__value">{formatPercentage(value)}</span>
    </span>
  );
};

LeakageProgress.propTypes = {
  value: PropTypes.oneOfType([PropTypes.string, PropTypes.number, PropTypes.object]),
};

LeakageProgress.defaultProps = {
  value: 0,
};

/* -------------------------------------------------------------------------
 * NetworkLeakageDashboardPage — assembles the network leakage dashboard from independently
 * reusable widgets. No sidebar or header here; content-only.
 * ---------------------------------------------------------------------- */

const NetworkLeakageDashboardPage = () => {
  document.title = "Network Leakage | Omaha Food Lovers";

  const [overview, setOverview] = useState(null);
  const [loading, setLoading] = useState(true);
  const [errorMessage, setErrorMessage] = useState("");
  const [performancePeriod, setPerformancePeriod] = useState("weekly");
  const [customDateFrom, setCustomDateFrom] = useState("");
  const [customDateTo, setCustomDateTo] = useState("");
  const [periodMenuOpen, setPeriodMenuOpen] = useState(false);
  const [memberRows, setMemberRows] = useState([]);
  const [restaurantRows, setRestaurantRows] = useState([]);
  const [memberPage, setMemberPage] = useState(1);
  const [memberHasMore, setMemberHasMore] = useState(false);
  const [memberLoadingMore, setMemberLoadingMore] = useState(false);
  const [restaurantPage, setRestaurantPage] = useState(1);
  const [restaurantHasMore, setRestaurantHasMore] = useState(false);
  const [restaurantLoadingMore, setRestaurantLoadingMore] = useState(false);
  const memberPaginationRef = useRef(getPagination(null));
  const restaurantPaginationRef = useRef(getPagination(null));

  const fetchOverview = useCallback(async ({ memberPage = 1, restaurantPage = 1, appendTarget = null } = {}) => {
    if (appendTarget === "member") {
      setMemberLoadingMore(true);
    } else if (appendTarget === "restaurant") {
      setRestaurantLoadingMore(true);
    } else {
      setLoading(true);
      setMemberRows([]);
      setRestaurantRows([]);
      setMemberPage(1);
      setMemberHasMore(false);
      setRestaurantPage(1);
      setRestaurantHasMore(false);
    }

    setErrorMessage("");

    try {
      const query = getOverviewQuery(performancePeriod, customDateFrom, customDateTo, {
        member_page: memberPage,
        member_per_page: PAGE_SIZE,
        restaurant_page: restaurantPage,
        restaurant_per_page: PAGE_SIZE,
      });
      const response = await get(
        `${endpoints.adminRecruitmentLeakageOverview}?${query}`
      );

      const payload = extractPayload(response);
      const nextMemberRows = extractRows(payload?.member_leakage_overview);
      const nextRestaurantRows = extractRows(payload?.restaurant_leakage_overview);
      const nextMemberPagination = getPagination(payload?.member_leakage_overview, nextMemberRows);
      const nextRestaurantPagination = getPagination(payload?.restaurant_leakage_overview, nextRestaurantRows);
      const memberCanLoadMore = nextMemberPagination.has_more && nextMemberRows.length > 0;
      const restaurantCanLoadMore = nextRestaurantPagination.has_more && nextRestaurantRows.length > 0;

      setOverview(payload);

      if (appendTarget === "member") {
        setMemberRows((rows) => appendUniqueRows(rows, nextMemberRows, getMemberRowKey));
        setMemberPage(nextMemberPagination.current_page);
        setMemberHasMore(memberCanLoadMore);
        memberPaginationRef.current = { ...nextMemberPagination, has_more: memberCanLoadMore };
      } else if (appendTarget === "restaurant") {
        setRestaurantRows((rows) => appendUniqueRows(rows, nextRestaurantRows, getRestaurantRowKey));
        setRestaurantPage(nextRestaurantPagination.current_page);
        setRestaurantHasMore(restaurantCanLoadMore);
        restaurantPaginationRef.current = { ...nextRestaurantPagination, has_more: restaurantCanLoadMore };
      } else {
        setMemberRows(nextMemberRows);
        setRestaurantRows(nextRestaurantRows);
        setMemberPage(nextMemberPagination.current_page);
        setMemberHasMore(memberCanLoadMore);
        memberPaginationRef.current = { ...nextMemberPagination, has_more: memberCanLoadMore };
        setRestaurantPage(nextRestaurantPagination.current_page);
        setRestaurantHasMore(restaurantCanLoadMore);
        restaurantPaginationRef.current = { ...nextRestaurantPagination, has_more: restaurantCanLoadMore };
      }
    } catch (error) {
      console.error("Failed to fetch network leakage overview:", error);
      setErrorMessage(extractErrorMessage(error) || "Unable to load network leakage overview.");
      if (!appendTarget) {
        setOverview(null);
        setMemberRows([]);
        setRestaurantRows([]);
      }
    } finally {
      if (appendTarget === "member") {
        setMemberLoadingMore(false);
      } else if (appendTarget === "restaurant") {
        setRestaurantLoadingMore(false);
      } else {
        setLoading(false);
      }
    }
  }, [performancePeriod, customDateFrom, customDateTo]);

  useEffect(() => {
    fetchOverview();
  }, [fetchOverview]);

  const rawSummaryCards = overview?.summary_cards;
  const summaryCardsData = useMemo(() => rawSummaryCards ?? [], [rawSummaryCards]);
  const selectedPeriodLabel = SUMMARY_PERIOD_LABELS[performancePeriod] || SUMMARY_PERIOD_LABELS.all_time;
  const totalDiningSpendCard = useMemo(() => getSummaryCard(summaryCardsData, "total_dining_spend"), [summaryCardsData]);
  const partnerSpendCard = useMemo(() => getSummaryCard(summaryCardsData, "partner_spend"), [summaryCardsData]);
  const nonPartnerSpendCard = useMemo(() => getSummaryCard(summaryCardsData, "non_partner_spend"), [summaryCardsData]);
  const leakageRateCard = useMemo(() => getSummaryCard(summaryCardsData, "leakage_rate"), [summaryCardsData]);

  const summaryCards = [
    {
      key: "total_dining_spend",
      label: "Total Dining Spend",
      value: formatMoney(getSummaryValue(summaryCardsData, ["total_dining_spend"])),
      deltaPct: totalDiningSpendCard.comparison_percent,
      color: "#1ba97f",
      icon: <SpendIcon />,
      iconBg: "#e7faf3",
    },
    {
      key: "partner_spend",
      label: "Partner Spend",
      value: formatMoney(getSummaryValue(summaryCardsData, ["partner_spend"])),
      deltaPct: partnerSpendCard.comparison_percent,
      color: "#3b82f6",
      icon: <img src={peopleOrangeIcon} alt="" className="network-leakage-stat-icon" />,
      iconBg: "var(--bg-light-blue)",
    },
    {
      key: "non_partner_spend",
      label: "Non-Partner Spend",
      value: formatMoney(getSummaryValue(summaryCardsData, ["non_partner_spend"])),
      deltaPct: nonPartnerSpendCard.comparison_percent,
      color: "#f59e0b",
      icon: <img src={locationOrangeIcon} alt="" className="network-leakage-stat-icon" />,
      iconBg: "#fff4e8",
    },
    {
      key: "leakage_rate",
      label: "Leakage Rate",
      value: formatPercentage(getSummaryValue(summaryCardsData, ["leakage_rate", "leakage_percentage"])),
      deltaPct: leakageRateCard.comparison_percent,
      color: "#e23d3d",
      icon: <PercentIcon />,
      iconBg: "var(--bg-light-red)",
    },
  ];

  const memberColumns = [
    { key: "member", label: "Member", width: "1.3fr", render: (row) => getFirstValue(row, ["member", "member_name", "name", "full_name"]) },
    { key: "member_email", label: "Member Email", width: "1.7fr", render: (row) => getFirstValue(row, ["member_email", "email"]) },
    { key: "total_dining_spend", label: "Total Dining Spend", width: "1fr", align: "right", render: (row) => formatMoney(row.total_dining_spend) },
    { key: "partner_spend", label: "Partner Spend", width: "1fr", align: "right", render: (row) => formatMoney(row.partner_spend) },
    { key: "non_partner_spend", label: "Non-Partner Spend", width: "1fr", align: "right", render: (row) => formatMoney(row.non_partner_spend) },
    { key: "leakage_percentage", label: "Leakage %", width: "1fr", render: (row) => <LeakageProgress value={getFirstValue(row, ["leakage_percentage", "leakage_rate", "leakage_percent"], 0)} /> },
    { key: "last_visit", label: "Last Visit", width: "1fr", render: (row) => formatLastVisitDate(getFirstValue(row, ["last_visit", "last_transaction_at", "last_visited_at", "last_visit_at"])) },
  ];

  const restaurantColumns = [
    { key: "restaurant_name", label: "Restaurant Name", width: "1.5fr", render: (row) => getFirstValue(row, ["restaurant_name", "restaurant", "name"]) },
    { key: "member_name", label: "Member Name", width: "1.2fr", render: (row) => getFirstValue(row, ["member_name", "member", "name"]) },
    { key: "member_email", label: "Member Email", width: "1.7fr", render: (row) => getFirstValue(row, ["member_email", "email"]) },
    { key: "total_transactions", label: "Total Transactions", width: "1fr", align: "right", render: (row) => formatNumber(row.total_transactions) },
    { key: "spend", label: "Spend", width: "1fr", align: "right", render: (row) => formatMoney(row.spend) },
    { key: "leakage_percentage", label: "Leakage %", width: "1fr", render: (row) => <LeakageProgress value={getFirstValue(row, ["leakage_percentage", "leakage_rate", "leakage_percent"], 0)} /> },
    { key: "last_visit", label: "Last Visit", width: "1fr", render: (row) => formatLastVisitDate(getFirstValue(row, ["last_visit", "last_transaction_at", "last_visited_at", "last_visit_at"])) },
  ];

  const loadMoreMembers = useCallback(() => {
    if (!memberLoadingMore && memberHasMore) {
      const nextPage = memberPage + 1;
      fetchOverview({
        memberPage: nextPage,
        restaurantPage: restaurantPage,
        appendTarget: "member",
      });
    }
  }, [memberLoadingMore, memberHasMore, memberPage, restaurantPage, fetchOverview]);

  const loadMoreRestaurants = useCallback(() => {
    if (!restaurantLoadingMore && restaurantHasMore) {
      const nextPage = restaurantPage + 1;
      fetchOverview({
        memberPage: memberPage,
        restaurantPage: nextPage,
        appendTarget: "restaurant",
      });
    }
  }, [restaurantLoadingMore, restaurantHasMore, memberPage, restaurantPage, fetchOverview]);



  return (
    <div className="page-content rp-page-content">
      <style>{`
        .network-leakage-stats-grid {
          display: grid;
          gap: 16px;
          grid-template-columns: repeat(4, minmax(0, 1fr));
          margin-bottom: 24px;
        }
        .network-leakage-error {
          color: #dc2626;
          font-weight: 600;
          margin: 0 0 16px;
        }
        .network-leakage-progress {
          align-items: center;
          display: flex;
          gap: 8px;
          min-width: 120px;
        }
        .network-leakage-progress__bar {
          background: #f3f4f6;
          border-radius: 999px;
          display: inline-flex;
          height: 8px;
          overflow: hidden;
          width: 72px;
        }
        .network-leakage-progress__fill {
          background: #ff7a00;
          border-radius: inherit;
          display: block;
          height: 100%;
        }
        .network-leakage-progress__value {
          color: #374151;
          font-size: 12px;
          font-weight: 700;
          white-space: nowrap;
        }
        .network-leakage-stat-icon {
          display: block;
          height: 20px;
          width: 20px;
        }
        .ar-period-btn {
          background: transparent;
          border: 0;
          border-radius: 999px;
          color: #6b7280;
          font-size: 13px;
          font-weight: 700;
          min-height: 30px;
          padding: 6px 12px;
          white-space: nowrap;
        }
        .ar-period-btn--active {
          background: #ff7a00;
          color: #fff;
        }
        @media (max-width: 991px) {
          .network-leakage-stats-grid {
            grid-template-columns: repeat(2, minmax(0, 1fr));
          }
        }
        @media (max-width: 575px) {
          .network-leakage-stats-grid {
            grid-template-columns: 1fr;
          }
        }
      `}</style>
      <Container fluid>
        <div className="stl-page">
          {/* ---- Header ---- */}
          <div className="adb-header" style={{ marginBottom: "24px" }}>
            <div>
              <h1 className="adb-header__title">Network Leakage</h1>
              <p className="adb-header__subtitle">Review network leakage by member and non-partner restaurant.</p>
            </div>
            <div style={{ display: "flex", gap: "8px", alignItems: "center" }}>
              <Dropdown isOpen={periodMenuOpen} toggle={() => setPeriodMenuOpen((o) => !o)}>
                <DropdownToggle tag="div">
                  <button className="adb-period-btn"><IconCalendar /> {getPeriodLabel(performancePeriod)} <IconChevronDown /></button>
                </DropdownToggle>
                <DropdownMenu end>
                  {Object.entries(SUMMARY_PERIOD_LABELS).map(([key, label]) => (
                    <DropdownItem key={key} active={performancePeriod === key} onClick={() => { setPerformancePeriod(key); if (key !== "custom") { setCustomDateFrom(""); setCustomDateTo(""); } fetchOverview(); }}>{label}</DropdownItem>
                  ))}
                </DropdownMenu>
              </Dropdown>
              {performancePeriod === "custom" && (
                <div style={{ display: "flex", gap: "8px", alignItems: "center" }}>
                  <input
                    type="date"
                    value={customDateFrom}
                    onChange={(e) => {
                      setCustomDateFrom(e.target.value);
                    }}
                    style={{ padding: "6px 10px", border: "1px solid #d1d5db", borderRadius: "6px", fontSize: "13px" }}
                  />
                  <span style={{ color: "#6b7280" }}>to</span>
                  <input
                    type="date"
                    value={customDateTo}
                    onChange={(e) => {
                      setCustomDateTo(e.target.value);
                    }}
                    style={{ padding: "6px 10px", border: "1px solid #d1d5db", borderRadius: "6px", fontSize: "13px" }}
                  />
                </div>
              )}
            </div>
          </div>
        </div>

        {errorMessage && <p className="network-leakage-error">{errorMessage}</p>}

        <div className="network-leakage-stats-grid">
          {summaryCards.map((card) => (
            <StatCard
              key={card.key}
              label={card.label}
              value={card.value}
              delta={null}
              deltaSubtitle={selectedPeriodLabel}
              periodLabel={selectedPeriodLabel}
              deltaPct={card.deltaPct}
              sparklineData={[]}
              sparklineColor={card.color}
              titleColor={card.color}
              icon={card.icon}
              iconBg={card.iconBg}
            />
          ))}
        </div>

        <Row className="g-3 align-items-stretch" style={{ marginBottom: 24 }}>
          <Col xl={12}>
            <div className="rp-card">
              <div className="rp-card__header">
                <div>
                  <h2 className="rp-card__title">Member Leakage Overview</h2>
                  <p className="rp-card__subtitle">Members spending the most at non-partner restaurants.</p>
                </div>
              </div>
              <DataTable
                columns={memberColumns}
                rows={memberRows}
                getRowKey={getMemberRowKey}
                loading={loading}
                compact={true}
                onLoadMore={loadMoreMembers}
                hasMore={memberHasMore}
                loadingMore={memberLoadingMore}
              />
            </div>
          </Col>
        </Row>

        <Row className="g-3 align-items-stretch" style={{ marginBottom: 24 }}>
          <Col xl={12}>
            <div className="rp-card">
              <div className="rp-card__header">
                <div>
                  <h2 className="rp-card__title">Restaurant Leakage Overview</h2>
                  <p className="rp-card__subtitle">Restaurants where members are spending the most outside the network.</p>
                </div>
              </div>
              <DataTable
                columns={restaurantColumns}
                rows={restaurantRows}
                getRowKey={getRestaurantRowKey}
                loading={loading}
                compact={true}
                onLoadMore={loadMoreRestaurants}
                hasMore={restaurantHasMore}
                loadingMore={restaurantLoadingMore}
              />
            </div>
          </Col>
        </Row>
      </Container>
    </div>
  );
};

NetworkLeakageDashboardPage.propTypes = {
  onViewAllLeakage: PropTypes.func,
};

NetworkLeakageDashboardPage.defaultProps = {
  onViewAllLeakage: () => {},
};

export default NetworkLeakageDashboardPage;
